import type CheckModel from '@/Models/Check/CheckModel';
import { type ConfirmedNomenclature } from '@/Models/ConfirmedNomenclature';
import { Label } from '@/Models/GenericWorklist/Label';
import { PatientRemark } from '@/Models/GenericWorklist/Remarks';
import type LockModel from '@/Models/Lock/LockModel';
import AddOrEditNewWordResult from '@/Models/NewWords/AddOrEditNewWordResult';
import type TextBlockModel from '@/Models/TextBlock/TextBlockModel';
import type ReportContentModel from '@/Models/Reports/ReportContentModel';
import type NewWordsOverviewViewModel from '@/Pages/NewWords/NewWordsOverviewViewModel';
import type TextBlockDetailViewModel from '@/Pages/TextBlockDetail/TextBlockDetailViewModel';
import type TextBlockOverviewViewModel from '@/Pages/TextBlockOverview/TextBlockOverviewViewModel';
import UserDefaultsModelDTO from '@/RazorTS/UserDefaults/DTO/UserDefaultsModelDTO';
import { OptionLists } from '@/Stores/UserDefaultsStore';
import moment from 'moment';
import { mapDTOToCustomWorklistSaveResult, type CustomWorklistSaveResult } from './Models/API/Worklist/CustomWorklistSaveResult';
import { SaveCustomWorklistToDashboardDTO } from './Models/API/WorklistsDashboard/SaveCustomWorklistToDashboardDTO';
import { WorklistsDashboardDataForUserDTO } from './Models/API/WorklistsDashboard/WorklistsDashboardDataForUserDTO';
import type AutocompletePhysiciansResult from './Models/AutocompletePhysiciansResult';
import { generalSearchMapper } from './Models/GeneralSearch';
import type CustomWorklistDTO from './Models/GenericWorklist/CustomWorklist';
import { mapCustomWorklistToDTO, mapDTOToCustomWorklist, type CustomWorklist } from './Models/GenericWorklist/CustomWorklist';
import { type Worklist, type WorklistParams } from './Models/GenericWorklist/WorklistParams';
import { type BulkReportingAction, type WorklistResult } from './Models/GenericWorklist/WorklistResult';
import { mapDTOToWorklistInitialData, type WorklistInitialData, type WorklistInitialDataDTO } from './Models/GenericWorklist/WorklistSideBarData';
import type { AboutInfo } from './Models/AboutInfo';
import type LoginData from './Models/LoginData';
import type LoginOptionsResult from './Models/LoginOptionsResult';
import MenuItem from './Models/MenuItem';
import type GetNewWordsResult from './Models/NewWords/GetNewWordsResult';
import type NewWord from './Models/NewWords/NewWord';
import type NewWordsFilter from './Models/NewWords/NewWordsFilter';
import type OrderNomenclature from './Models/OrderNomenclature';
import type Patient from './Models/Patient';
import type PatientStatus from './Models/PatientStatus';
import type PhysicianIDType from './Models/PhysicianIDType';
import type Prescription from './Models/Prescription';
import type Procedure from './Models/Procedure';
import type SelectListItem from './Models/SelectListItem';
import type SiteModel from './Models/SiteModel';
import { systemSettingsMapper, type SystemSettingsModel } from './Models/SystemSettings';
import type TextBlockResult from './Models/TextBlock/TextBlockResult';
import { userLoadedMapper, type UserModel } from './Models/User';
import type GroupLevel from './Pages/GenericWorklist/Components/GroupLevel';
import { GetProceduresForJustificationResult } from '@/Models/JustificationProcedure';
import { Group } from '@/Models/Shared/Group';
import UserGroupModel from '@/Models/PermissionsConfiguration/UserGroupModel';
import MenuItemPermissionsModel from '@/Models/PermissionsConfiguration/MenuItemPermissionsModel';
import RoleModel from '@/Models/PermissionsConfiguration/RoleModel';
import PermissionModel from '@/Models/PermissionsConfiguration/PermissionModel';
import { GroupModel } from '@/Models/PermissionsConfiguration/GroupModel';
import { AddUserModel } from '@/Models/PermissionsConfiguration/AddUserModel';
import { PopupProcedureReportInformation } from '@/Models/PopupProcedureReportInformation';
import { AddUserResultModel } from '@/Models/PermissionsConfiguration/AddUserResultModel';

export interface OKResponse {
    Ok: boolean;
}

const baseURL = `${window.location.protocol}//${window.location.hostname}${window.location.port !== '' ? ':' + window.location.port : ''}`;

// Shared
const legacyURL = (path: string): URL => {
    const legacyPrefix = 'legacy';

    if (path.startsWith('/')) {
        return new URL(`${legacyPrefix}${path}`, baseURL);
    }

    return new URL(`${legacyPrefix}/${path}`, baseURL);
};

const legacyAPIURL = (path: string): URL => {
    const apiPrefix = 'api';

    if (path.startsWith('/')) {
        return new URL(`${apiPrefix}${path}`, baseURL);
    }

    return new URL(`${apiPrefix}/${path}`, baseURL);
};

const coreURL = (path: string): URL => {
    const corePrefix = 'core';

    if (path.startsWith('/')) {
        return new URL(`${corePrefix}${path}`, baseURL);
    }

    return new URL(`${corePrefix}/${path}`, baseURL);
};

const APIURL = (path: string): URL => {
    const apiPrefix = 'core/api';

    if (path.startsWith('/')) {
        return new URL(`${apiPrefix}${path}`, baseURL);
    }

    return new URL(`${apiPrefix}/${path}`, baseURL);
};

const formFetch = async <TResponse, TErrorResponse>(url: URL, fetchOptions: RequestInit | undefined, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void): Promise<void> => {
    const response = await fetch(url, fetchOptions);
    if (response.ok) {
        onSuccess((await response.json()) as TResponse);
    }
    else {
        onFailure((await response.json()) as TErrorResponse);
    }
};

// User
const user = {
    updateFavorites: async (favorites: MenuItem[]): Promise<any> => {
        return await fetch(legacyAPIURL('user/favorites/update'), {
            method: 'POST',
            body: JSON.stringify(favorites),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        })
            .then(async (res) => await res.json());
    },
    getFavorites: async (): Promise<any> => await fetch(legacyAPIURL('user/favorites/get'), { credentials: 'include' }).then(async (res) => await res.json()),
    getUserDetails: async (): Promise<UserModel | null> => await fetch(legacyAPIURL('user/details'), { credentials: 'include' })
        .then(async (res) => {
            const model = await res.json();
            if (model == null) {
                return null;
            }
            return userLoadedMapper(model);
        }),
    // Although it might not make much sense initially, you need credentials: 'include' to make sure the browser sets the receiving ASP cookies
    login: async (data: LoginData): Promise<any> => await fetch(coreURL('Auth/Login'), {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    }),
    logout: async (): Promise<any> => await fetch(coreURL('Auth/Logout'), { credentials: 'include', method: 'GET' }),
    getUserDefaultsDTO: async (userId: string | undefined): Promise<UserDefaultsModelDTO> => await fetch(legacyAPIURL('user/details/defaultsModel' + (userId ? `?id=${userId}` : '')), { credentials: 'include' }).then(async (res) => await res.json()),
    getUserDefaultsHydrationData: async (): Promise<OptionLists> => await fetch(legacyAPIURL('user/details/defaultsHydrationData'), { credentials: 'include' }).then(async (res) => await res.json()),
    saveUserDefaultsNew: async <TResponse, TErrorResponse>(data: UserDefaultsModelDTO, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void) =>
        await formFetch<TResponse, TErrorResponse>(
            legacyURL('/UserDefaults/Save'),
            { credentials: 'include', method: 'POST', body: JSON.stringify({ id: data.User_UID, model: data }), headers: { 'Content-Type': 'application/json' } },
            onSuccess,
            onFailure,
        ),
    setDefaultDragonLanguage: async (lang: string): Promise<any> => await fetch(legacyAPIURL('user/details/setDefaultDragonLanguage?' + new URLSearchParams({ lang })), {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }),
};

// General
const general = {
    checkIfAllowedToOpen: async (path: string): Promise<any> => await fetch(legacyAPIURL('/session/CheckIfAllowedToOpen?' + new URLSearchParams({ url: path })), {
        credentials: 'include',
        method: 'GET',
    }).then(async (res) => await res.json()),
    getLoginOptions: async (): Promise<LoginOptionsResult> => await fetch(legacyAPIURL('login/options'), { method: 'GET' }).then(async (res) => await res.json()),
    getEquipmentForSite: async (siteID: string): Promise<any> => await fetch(legacyURL('/Login/GetEquipmentForSite?' + new URLSearchParams({ Site_UID: siteID })), { method: 'GET' }).then(async (res) => await res.json()),
    getAboutInfo: async (): Promise<AboutInfo> =>
        await fetch(legacyURL('/System/GetAboutRisSettings'), { credentials: 'include' })
            .then(async (res) => await res.json())
            .then((res: { data: { Tag: string; Value: string }[] }) => {
                const settingsObject: Record<string, string> = {};
                res.data.forEach((item) => {
                    settingsObject[item.Tag] = item.Value;
                });
                return {
                    CompanyName: settingsObject.CompanyName ?? '',
                    SupportMailAddress: settingsObject.SupportMailAddress ?? '',
                    SupportTelephone: settingsObject.SupportTelephone ?? '',
                    TeamViewerLink: settingsObject.TeamViewerLink ?? '',
                    Version: settingsObject.Version ?? '',
                    ReleaseDate: settingsObject.ReleaseDate ?? '',
                };
            }),
    getGeneralSettings: async (): Promise<SystemSettingsModel | null> => await fetch(legacyAPIURL('/settings'), { credentials: 'include' })
        .then(async (res) => {
            const model = await res.json();
            return systemSettingsMapper(model);
        }),
    getRazorView: async (path: string): Promise<any> => await fetch(legacyURL(path), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.text()),
    getTranslations: async (): Promise<any> => await fetch(legacyURL('Localisation/GetGlobalTranslations'), { credentials: 'include' }).then(async (res) => await res.json()).then((t) => JSON.parse(t)),
    getMenuItems: async (): Promise<any> => await fetch(legacyAPIURL('system/menuitems'), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    generalSearch: async (term: string): Promise<any> => await fetch(legacyURL(`Dashboard/GeneralSearch?term=${term}`), {
        method: 'POST',
        credentials: 'include',
    }).then(async (res) => {
        const model = await res.json();
        return generalSearchMapper(model);
    }),
    WCFData: async (): Promise<any> => await fetch('http://localhost:8850/Data', {
        headers: { 'Access-Control-Request-Private-Network': 'true' },
        method: 'GET',
    }).then(async (res) => await res.text()),
    getReportsToOpen: async (): Promise<any> => await fetch('http://localhost:8850/GetReportsToOpen').then(async (res) => await res.json()),
    openReport: async (data: any): Promise<any> => await fetch(legacyURL('/Reports/OpenReport'), {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    getOrdersToOpen: async (): Promise<any> => await fetch('http://localhost:8850/GetOrdersToOpen').then(async (res) => await res.json()),
    openOrders: async (data: any): Promise<any> => await fetch(legacyURL('/Orders/OpenOrders'), {
        credentials: 'include',
        body: JSON.stringify(data),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    closePACSForPatient: async (patientID: string): Promise<any> => await fetch(legacyURL('/Json/ClosePACSForPatient?' + new URLSearchParams({ Patient_UID: patientID })), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    closeActiveSpeech: async (): Promise<any> => await fetch(legacyURL('/Reports/CloseActiveSpeech'), {
        credentials: 'include',
        method: 'POST',
    }).then(async (res) => await res.json()),
    findPatientsByTerm: async (term: string): Promise<any> => await fetch(legacyURL('/Patients/FindPatientsByTerm?' + new URLSearchParams({ term })),
        { credentials: 'include', method: 'GET', headers: { 'Content-Type': 'application/json' } })
        .then(async (res) => await res.json()),
    getAppConfig: async (): Promise<any> => await fetch(APIURL('/runtimeconfig')).then(async (res) => await res.json()).then((json) => {
        window.appConfig = json;
    }).catch((err) => {
        throw new Error(err);
    }),
    getAllSites: async (): Promise<SiteModel[]> => await fetch(legacyAPIURL('/sites'), { credentials: 'include' }).then(async (res) => await res.json()),
};

// EIDC
const EIDC = {
    getPopupOptions: async (): Promise<any> => await fetch(legacyURL('/EIDC/GetPopupOptions'), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    checkWCFeID: async (): Promise<any> => await fetch(legacyURL('/EIDC/CheckWCFeID'), { credentials: 'include' }).then(async (res) => await res.json()),
    setSessionFromEIDPopup: async (): Promise<any> => await fetch(legacyURL('/EIDC/SetSessionFromEIDPopup'), { credentials: 'include' }),
    unsetSessionFromEIDPopup: async (): Promise<any> => await fetch(legacyURL('/EIDC/UnsetSessionFromEIDPopup'), { credentials: 'include' }),
    getOrdersForEIDPatient: async (patientID: string): Promise<any> => await fetch(legacyURL('/Home/GetOrdersForEIDPatient?' + new URLSearchParams({ Patient_UID: patientID })), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    })
        .then(async (res) => await res.json()),
    getSimilarEIDPatients: async (filterName: boolean, filterCity: boolean, filterDOB: boolean): Promise<any> => await fetch(legacyURL('/EIDC/GetSimilarEIDPatients?' + new URLSearchParams({
        FilterName: String(filterName),
        FilterCity: String(filterCity),
        FilterDOB: String(filterDOB),
    })),
    { credentials: 'include', method: 'GET', headers: { 'Content-Type': 'application/json' } })
        .then(async (res) => await res.json()),
};

// Locks
const locks = {
    tryCreateLockFile: async (data: any): Promise<any> => await fetch(legacyURL('/Locks/TryCreateLockFile'), {
        credentials: 'include',
        body: JSON.stringify(data),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    stealLock: async (data: any): Promise<any> => await fetch(legacyURL('/Locks/StealLock'), {
        credentials: 'include',
        body: JSON.stringify(data),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }),
    releaseLocks: async (ids: number[], isCloseAll: boolean): Promise<any> => await fetch(legacyURL('/Locks/ReleaseLocks'), {
        credentials: 'include',
        body: JSON.stringify({ LockUIDs: ids, isCloseAll }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }),
    getCurrentLocks: async (): Promise<LockModel[]> => await fetch(coreURL('/Lock/GetLocksOverview'), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    releaseLockByID: async (id: number): Promise<void> => {
        await fetch(coreURL('/Lock/UnlockFileByItsID'), {
            credentials: 'include',
            body: JSON.stringify({ LockUid: id }),
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
    },
    checkAndDeleteExpiredLocks: async (): Promise<void> => {
        await fetch(coreURL('/Lock/CheckAndDeleteExpiredLocks'), { credentials: 'include', method: 'POST', headers: { 'Content-Type': 'application/json' } });
    },
};

// Checks
const checks = {
    getChecksOverview: async (): Promise<CheckModel[]> => await fetch(coreURL('/Check/GetChecksOverview'), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    get: async (id: string | number): Promise<CheckModel | null> => await fetch(coreURL(`/Check/Get/${id}`), {
        credentials: 'include',
        method: 'GET',
    }).then(async (res) => await res.json()),
    add: async (data: CheckModel): Promise<any> => await fetch(coreURL('/Check/AddNewCheck'), {
        credentials: 'include',
        body: JSON.stringify(data),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    save: async (data: CheckModel): Promise<any> => await fetch(coreURL('/Check/EditCheckDetailItem'), {
        credentials: 'include',
        body: JSON.stringify(data),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
};

// Consolidate and resend page
const eFact = {
    getBatchesToConsolidate: async (date: string, onlyFixed: boolean): Promise<any> => await fetch(legacyURL('EFact/GetDataConsolidateAndResendEfactBatches?' + new URLSearchParams({
        beforeDateString: date,
        onlyFixed: onlyFixed.toString(),
    })), { credentials: 'include' }).then(async (res) => await res.json()),
    consolidateAndResendEFactBatches: async (date: string, onlyFixed: boolean): Promise<any> => {
        return await fetch(legacyURL('EFact/ConsolidateAndResendEfactBatches'), {
            method: 'POST',
            body: JSON.stringify({
                date,
                onlyFixed,
            }),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        })
            .then(async (res) => await res.json());
    },
    // Pending Batches page
    getPendingEFactBatches: async (): Promise<any> => await fetch(legacyURL('/Efact/GetPendingBatches'), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    cancelRunningEFactBatch: async (id: number): Promise<any> => await fetch(legacyURL('/Efact/CancelRunningEFactBatch?' + new URLSearchParams({ batchUID: id + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    moveBatchFirstInQueue: async (id: number): Promise<any> => await fetch(legacyURL('/Efact/MoveFirstInQueue'), {
        body: JSON.stringify({ batchID: id }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    moveBatchUpInQueue: async (id: number): Promise<any> => await fetch(legacyURL('/Efact/MoveUpInQueue'), {
        body: JSON.stringify({ batchID: id }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    moveBatchDownInQueue: async (id: number): Promise<any> => await fetch(legacyURL('/Efact/MoveDownInQueue'), {
        body: JSON.stringify({ batchID: id }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    // Previous invoicing batches page
    getPreviousInvoicingBatches: async (filter: string, page: number, amountToShow: number): Promise<any> => await fetch(legacyURL('/Invoices/GetPreviousBatches?' + new URLSearchParams({
        filter,
        type: '1',
        page: page + '',
        amountToShow: amountToShow + '',
    })), { method: 'GET', credentials: 'include' }).then(async (res) => await res.json()),
    getOrdersForPreviousInvoicingBatch: async (ID: number): Promise<any> => await fetch(legacyURL('/Invoices/GetOrdersInBatch?' + new URLSearchParams({ Batch_UID: ID + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    getErrorsForPreviousInvoicingBatch: async (batchID: number): Promise<any> => await fetch(legacyURL('/Invoices/GetErrorsForBatch?' + new URLSearchParams({ Batch_UID: batchID + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    unlinkAllOrdersFromBatch: async (batchID: number, deleteInvoices: boolean): Promise<any> => await fetch(legacyURL('/Invoices/UnlinkOrdersFromBatch'), {
        body: JSON.stringify({
            batch_UID: batchID,
            deleteInvoices,
        }), headers: { 'Content-Type': 'application/json' }, method: 'POST', credentials: 'include',
    }).then(async (res) => await res.json()),
    unlinkOrderFromBatch: async (orderID: number, deleteInvoice: boolean): Promise<any> => await fetch(legacyURL('/Invoices/DeleteOrderFromBatch'), {
        body: JSON.stringify({
            Order_UID: orderID,
            DeleteInvoices: deleteInvoice,
        }), headers: { 'Content-Type': 'application/json' }, method: 'POST', credentials: 'include',
    }).then(async (res) => await res.json()),
    getInvoiceBatchHistoryOfOrder: async (orderID: number): Promise<any> => await fetch(legacyURL('/Invoices/GetInvoiceBatchHistoryOfOrder?' + new URLSearchParams({ Order_UID: orderID + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    // EFactTool
    getNomenclaturesForBatchByIOAndFilename: async (io: string, filename: string, creationDate: Date): Promise<ConfirmedNomenclature[]> => await fetch(coreURL('/EFact/GetNomenclaturesForTFacFile?' + new URLSearchParams({
        io,
        filename,
        creationDate: moment(creationDate).format('YYYY-MM-DD'),
    })), { method: 'GET', credentials: 'include' })
        .then(async (res) => {
            if (res.status === 500) {
                throw await res.text();
            }
            return await res.json();
        }),
};

// Worklist
const worklist = {
    get: async (filter: WorklistParams, signal?: AbortSignal): Promise<WorklistResult> =>
        await fetch(coreURL('/Worklist/Get'), {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(filter),
            headers: { 'Content-Type': 'application/json' },
            signal,
        }).then(async (res) => await res.json()),
    saveCustomWorklist: async (
        name: string,
        groupLevel: GroupLevel,
        groupID: number | undefined,
        description: string | undefined,
        worklist: Worklist,
    ): Promise<CustomWorklistSaveResult> =>
        await fetch(coreURL('/Worklist/Save'), {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ name, description, groupLevel, groupID, worklist }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then(async (res) => await res.json())
            .then((dto) => mapDTOToCustomWorklistSaveResult(dto)),
    updateCustomWorklist: async (customWorklist: CustomWorklist): Promise<CustomWorklistSaveResult> =>
        await fetch(coreURL('/Worklist/UpdateFilter'), {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(mapCustomWorklistToDTO(customWorklist)),
            headers: { 'Content-Type': 'application/json' },
        })
            .then(async (res) => await res.json())
            .then((dto) => mapDTOToCustomWorklistSaveResult(dto)),
    updateCustomWorklistCollapsed: async (worklistId: number, collapsed: boolean): Promise<void> => {
        const response = await fetch(coreURL('/Worklist/UpdateWorklistConfig'), {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                WorklistId: worklistId,
                UserConfig: {
                    CollapsedState: collapsed,
                },
            }),
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
            throw new Error(`UpdateWorklistConfig failed: ${response.status} ${response.statusText}`);
        }
    },
    getSavedWorklists: async (): Promise<CustomWorklist[]> =>
        await fetch(coreURL('/Worklist/GetSaved'), { credentials: 'include' })
            .then(async (res) => (await res.json()) as CustomWorklistDTO[])
            .then((dtos) => dtos.map((dto) => mapDTOToCustomWorklist(dto))),
    deleteFilter: async (id: number): Promise<boolean> =>
        await fetch(coreURL(`/Worklist/DeleteFilter?id=${id}`), { credentials: 'include', method: 'POST', headers: { 'Content-Type': 'application/json' } }).then(
            async (res) => await res.json(),
        ),
    getInitialPageData: async (signal?: AbortSignal): Promise<WorklistInitialData> =>
        await fetch(coreURL('/worklist/initialdata'), { credentials: 'include', signal })
            .then(async (res) => (await res.json()) as WorklistInitialDataDTO)
            .then((dto) => mapDTOToWorklistInitialData(dto)),
    startBulkSession: async (actions: BulkReportingAction[]): Promise<{ AllOk: boolean; Message?: string }> => {
        const response = await fetch(legacyURL('/Session/NewBulkSessionFromWorklist'), {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(actions),
        });
        return response.json() as Promise<{ AllOk: boolean; Message?: string }>;
    },
};

const worklistDashboard = {
    deleteCustomWorklistFromUserDashboard: async (customWorklistID: number, requestInit?: RequestInit): Promise<WorklistsDashboardDataForUserDTO> => {
        const params = new URLSearchParams({
            customWorklistID: customWorklistID.toString(),
        });

        const response = await fetch(
            coreURL(`/user/worklistsdashboard?${params}`), {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                method: 'DELETE',
                ...requestInit,
            });
        const body = await response.json() as WorklistsDashboardDataForUserDTO;
        return body;
    },
    getWorklistDashboardDataForCurrentUser: async (requestInit?: RequestInit): Promise<WorklistsDashboardDataForUserDTO> => {
        const response = await fetch(
            coreURL(`/user/worklistsdashboard`), {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                method: 'GET',
                ...requestInit,
            });
        const body = await response.json() as WorklistsDashboardDataForUserDTO;
        return body;
    },
    saveCustomWorklistToUserDashboard: async (saveToDashboardData: SaveCustomWorklistToDashboardDTO, requestInit?: RequestInit): Promise<WorklistsDashboardDataForUserDTO> => {
        const response = await fetch(
            coreURL(`/user/worklistsdashboard`), {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                method: 'POST',
                body: JSON.stringify(saveToDashboardData),
                ...requestInit,
            });
        const body = await response.json() as WorklistsDashboardDataForUserDTO;
        return body;
    },
};

// Report TextBlocks
const textblocks = {
    getReportingTextBlocksForRadiologist: async (orderProcedureIDs: number[]): Promise<any> => await fetch(legacyAPIURL('/textblocks/GetReportingTextBlocksForRadiologist'), {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ orderProcedureIDs }),
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    getOverview: async (): Promise<TextBlockResult[]> => await fetch(legacyAPIURL('/textblocks/GetTextBlocksOverview'), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    exportAllTextblocksToG2: async (): Promise<any> => await fetch(legacyURL('/System/ExportAllTextblocksToG2'), {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(async (res) => await res.json()),
    getDetailTextBlock: async (ID: number): Promise<TextBlockModel> => await fetch(legacyAPIURL('/textblocks/detail?' + new URLSearchParams({ ID: ID + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    editTextBlock: async (currentID: number, textBlock: TextBlockModel): Promise<any> =>
        await fetch(legacyAPIURL('/textblocks/edit'), {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ ID: currentID, textBlock }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then(async (res) => await res.json()),
    addNewTextBlock: async (textBlock: TextBlockModel): Promise<any> =>
        await fetch(legacyAPIURL('/textblocks/add'), {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(textBlock),
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        })
            .then(async (res) => await res.json()),
    deleteTextBlock: async (ID: number): Promise<any> =>
        await fetch(legacyAPIURL('/textblocks/delete?ID=' + ID), {
            credentials: 'include',
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        }).then(async (res) => await res.json()),

};
// Physicians
const physicians = {
    getPhysicianIDs: async (ID: number): Promise<PhysicianIDType[]> => await fetch(legacyURL('/Physicians/GetPhysicianIDs?' + new URLSearchParams({ id: ID + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),

    checkRizivRegex: async (riziv: string): Promise<any> => await fetch(legacyURL('/Physicians/CheckRIZIVRegex?' + new URLSearchParams({ riziv })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
};
// Identifiers
const identifiers = {
    getIdentifierTypes: async (type: number): Promise<any> => await fetch(legacyURL('/Json/GetIdentifierTypes?' + new URLSearchParams({ type: type + '' })), {
        method: 'GET',
        credentials: 'include',
    }).then(async (res) => await res.json()),
    getRIZIVFormat: async (): Promise<any> => await fetch(legacyURL('/Json/GetRIZIVFormat'), { credentials: 'include' }).then(async (res) => await res.json()),
};

// bulk session
const bulkSession = {
    clearBulkSession: async (typeValue: number): Promise<any> => await fetch(legacyURL('/Reports/DeleteActiveBulkDictations'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ Type: typeValue }),
    }),
    skipOrderInBulkSession: async (orderId: number, reportId?: number): Promise<{ AllOk: boolean; Message?: string }> => {
        const response = await fetch(legacyURL('/Session/SetMixedAsSkippedInBulkSession'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ orderId, reportId: reportId ?? null }),
        });
        return response.json() as Promise<{ AllOk: boolean; Message?: string }>;
    },
};

const setup = {
    textBlocks: {
        textBlockOverview: async (): Promise<TextBlockOverviewViewModel> => await fetch(legacyAPIURL('/textblocks/overviewSetup'), {
            credentials: 'include',
            method: 'GET',
        }).then(async (res) => await res.json()),
        textBlockDetail: async (): Promise<TextBlockDetailViewModel> => await fetch(legacyAPIURL('/textblocks/detailSetup'), {
            credentials: 'include',
            method: 'GET',
        }).then(async (res) => await res.json()),
    },
    newWords: {
        newWordsOverview: async (): Promise<NewWordsOverviewViewModel> => await fetch(coreURL('/newwords/overviewSetup'), {
            credentials: 'include',
            method: 'GET',
        }).then(async (res) => await res.json()),
    },
};

// Motivation types
const getSelectDropDownForMotivations = async (): Promise<any> => await fetch(legacyURL('/Json/GetSelectDropDownForMotivations'), {
    method: 'GET',
    credentials: 'include',
}).then(async (res) => await res.json());

const getNomenclatureForTerm = async (number: string, patientStatus: PatientStatus, datetimeImplementation: string): Promise<OrderNomenclature[]> => await fetch(legacyURL('/Nomenclature/GetNomenclatureForTerm?' + new URLSearchParams({
    nomenclatureNumber: number + '',
    patientStatus: String.fromCharCode(Number(patientStatus)),
    datetime_implementation: datetimeImplementation,
})), { method: 'GET', credentials: 'include' }).then(async (res) => await res.json());

// Creating presctiptions
const getPhysicianForTerm = async (term: string): Promise<AutocompletePhysiciansResult> => await fetch(coreURL('/physician/autocomplete?' + new URLSearchParams({ term })), {
    method: 'GET',
    credentials: 'include',
}).then(async (res) => await res.json());
const getDepartmentForTerm = async (term: string): Promise<SelectListItem[]> => await fetch(legacyURL('/Json/AutoCompleteDepartments?' + new URLSearchParams({ term })), {
    method: 'GET',
    credentials: 'include',
}).then(async (res) => await res.json());
const getPatientsForTerm = async (term: string): Promise<Patient[]> => await fetch(legacyURL('/Patients/AutoCompletePatients?' + new URLSearchParams({ term })), {
    method: 'GET',
    credentials: 'include',
}).then(async (res) => await res.json());
const getProceduresForTerm = async (term: string, patientID: number): Promise<Procedure[]> => await fetch(legacyURL('/Json/AutoCompleteProcedures?' + new URLSearchParams({
    term,
    patientID: patientID + '',
})), { method: 'GET', credentials: 'include' }).then(async (res) => await res.json());
const sendPrescription = async (placerModel: Prescription): Promise<any> => await fetch(legacyURL('/Prescriptions/Create'), {
    body: JSON.stringify({ placerModel }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    credentials: 'include',
}).then(async (res) => await res.json());

// New words
const newWords = {
    getNewWords: async (filter: NewWordsFilter | undefined, status: number, page: number, amountToShow: number): Promise<GetNewWordsResult> => await fetch(coreURL('/newwords/get'),
        {
            body: JSON.stringify({ Filter: filter, Status: status, Page: page, AmountToShow: amountToShow }),
            headers:
                {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            method: 'POST',
            credentials: 'include',
        }).then(async (res) => await res.json()),
    addOrEditNewWord: async (newWord: NewWord): Promise<AddOrEditNewWordResult> => await fetch(coreURL('/newwords/addoredit'),
        {
            body: JSON.stringify({ ...newWord }),
            headers:
                {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            method: 'POST',
            credentials: 'include',
        }).then(async (res) => await res.json()),
    approveNewWord: async (newWordId: number): Promise<any> => await fetch(coreURL(`/newwords/approve?id=${newWordId}`),
        {
            headers:
                {
                    'Content-Type': 'application/json',
                },
            method: 'POST',
            credentials: 'include',
        }).then(async (res) => {
        return res;
    }),
    ignoreNewWord: async (newWordId: number): Promise<any> => await fetch(coreURL(`/newwords/ignore?id=${newWordId}`),
        {
            headers:
                {
                    'Content-Type': 'application/json',
                },
            method: 'POST',
            credentials: 'include',
        }).then(async (res) => {
        return res;
    }),
};

// Statuses
const statuses = {
    getJustificationDropdown: async (): Promise<{ Id: number; Name: string; Disabled: boolean }[]> => {
        let result: { Id: number; Name: string; Disabled: boolean }[] = [];

        await formFetch<{ Id: number; Name: string; Disabled: boolean }[], unknown>(
            coreURL('/Status/GetJustificationDropdownStatuses'),
            {
                credentials: 'include',
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            },
            (res) => {
                result = res;
            },
            () => {
                console.error('Failed to fetch justification dropdown');
                result = [];
            },
        );

        return result;
    },

};

// Remarks
const remarks = {
    getRemarksForWorklist: async (
        patientId: number,
        signal?: AbortSignal,
    ): Promise<PatientRemark> => {
        const params = new URLSearchParams({
            patientId: patientId.toString(),
        });

        return await fetch(coreURL(`/Remarks/GetPatientAllergiesAndParticularitiesRemarks?${params}`), {
            credentials: 'include',
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal,
        }).then(async (res) => {
            if (!res.ok) {
                console.error('Failed to fetch patient remarks');
            }
            return await res.json();
        });
    },
};

// Justifications
const justifications = {
    getProceduresForJustification: async (
        orderUID: number,
        patientUID: number,
    ): Promise<GetProceduresForJustificationResult> => {
        const params = new URLSearchParams({
            Order_UID: orderUID.toString(),
            Patient_UID: patientUID.toString(),
        });

        let response: GetProceduresForJustificationResult = {
            AllOk: false,
            List: [],
        };

        await formFetch<GetProceduresForJustificationResult, unknown>(
            legacyURL(`/System/GetProceduresForJustification?${params}`),
            {
                credentials: 'include',
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            },
            (res) => {
                response = res;
            },
            () => {
                response = {
                    AllOk: false,
                    List: [],
                };
            },
        );

        return response;
    },
    saveJustifications: async <TResponse, TErrorResponse>(
        justificationModels: {
            OrderProcedure_UID: number;
            Justification: string;
            ChangeToStatus: number;
            Required: boolean;
        }[],
        onSuccess: (res: TResponse) => void,
        onFailure: (res: TErrorResponse) => void,
    ) =>
        await formFetch<TResponse, TErrorResponse>(
            legacyURL('/System/SaveJustifications'),
            {
                credentials: 'include',
                method: 'POST',
                body: JSON.stringify(justificationModels),
                headers: { 'Content-Type': 'application/json' },
            },
            onSuccess,
            onFailure,
        ),
};

const radiologists = {
    search: async (searchTerm: string): Promise<{ Id: number; Name: string; FirstName: string; FullName: string } []> => {
        const res = await fetch(
            coreURL('/radiologist/autocomplete?' + new URLSearchParams({ term: searchTerm }).toString()),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json()) as { Id: number; Name: string; FirstName: string; FullName: string } [];
    },
};

const procedures = {
    procedureCodes: async (searchTerm: string): Promise<string[]> => {
        const res = await fetch(
            coreURL('/procedurecode/GetBySearch?' + new URLSearchParams({ term: searchTerm }).toString()),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json()) as string[];
    },
};

const technicians = {
    search: async (searchTerm: string): Promise<{ ID: number; FullText: string }[]> => {
        const res = await fetch(
            legacyURL('/Json/AutoCompleteTechnicians?' + new URLSearchParams({ term: searchTerm }).toString()),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json()) as { ID: number; FullText: string }[];
    },
};

const users = {
    GetByFullUsername: async (searchTerm: string): Promise<{ Id: number; FullName: string }[]> => {
        const res = await fetch(
            coreURL('/user/autocomplete?' + new URLSearchParams({ term: searchTerm }).toString()),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json()) as { Id: number; FullName: string }[];
    },
};

const orders = {
    markAsReady: async (orderIds: number[]): Promise<boolean> =>
        await fetch(coreURL('/Order/MarkAsReadyForInvoicing'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(orderIds),
        }).then(async (res) => {
            return await res.json() as boolean;
        }),
};

const labels = {
    getAll: async (): Promise<Label[]> => {
        const res = await fetch(coreURL('/Label/GetAll'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as Label[]);
    },
    assignLabelToOrders: async <TResponse, TErrorResponse>(data: { labelId: number; orderIds: number[] }, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void) =>
        await formFetch<TResponse, TErrorResponse>(
            coreURL('/Label/AssignLabelToOrders'),
            { credentials: 'include', method: 'POST', body: JSON.stringify({ labelId: data.labelId, orderIds: data.orderIds }), headers: { 'Content-Type': 'application/json' } },
            onSuccess,
            onFailure,
        ),
    unassignLabelFromOrders: async <TResponse, TErrorResponse>(data: { labelId: number; orderIds: number[] }, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void) =>
        await formFetch<TResponse, TErrorResponse>(
            coreURL('/Label/UnassignLabelFromOrders'),
            { credentials: 'include', method: 'POST', body: JSON.stringify({ labelId: data.labelId, orderIds: data.orderIds }), headers: { 'Content-Type': 'application/json' } },
            onSuccess,
            onFailure,
        ),
};

const nomenclatures = {
    getOverview: async<TResponse, TErrorResponse>(data: { filter: string; page: number; amountToShow: number; sortColumn: string; sortOrder: string }, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void) =>
        await formFetch<TResponse, TErrorResponse>(
            legacyURL('/Nomenclature/GetNomenclaturesOverview?' + new URLSearchParams({
                filter: data.filter,
                page: data.page as unknown as string,
                amountToShow: data.amountToShow as unknown as string,
                sortColumn: data.sortColumn,
                sortOrder: data.sortOrder,
            }).toString()),
            { credentials: 'include', method: 'GET', headers: { 'Content-Type': 'application/json' } },
            onSuccess,
            onFailure,
        ),
    delete: async (id: number): Promise<void> => {
        await fetch(
            legacyURL('/Nomenclature/DeleteNomenclature'), {
                credentials: 'include', method: 'POST',
                body: JSON.stringify({ id }),
                headers: { 'Content-Type': 'application/json' },
            },
        );
    },
};

const groups = {
    getAll: async (): Promise<Group[]> => {
        const res = await fetch(coreURL('/Group/get'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as Group[]);
    },

};

const permissions = {
    getAllUsers: async (): Promise<UserGroupModel[]> => {
        const res = await fetch(coreURL('/permissions/users'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as UserGroupModel[]);
    },
    saveUser: async (data: UserGroupModel) => {
        await fetch(
            coreURL('/permissions/user'),
            { credentials: 'include', method: 'PATCH', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } },
        );
    },
    getMenuItems: async (): Promise<MenuItemPermissionsModel[]> => {
        const res = await fetch(coreURL('/permissions/menuitems'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as MenuItemPermissionsModel[]);
    },
    getRoles: async (): Promise<RoleModel[]> => {
        const res = await fetch(coreURL('/permissions/roles'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as RoleModel[]);
    },
    getPermissions: async (): Promise<PermissionModel[]> => {
        const res = await fetch(coreURL('/permissions/permissions'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as PermissionModel[]);
    },
    getGroups: async (): Promise<GroupModel[]> => {
        const res = await fetch(coreURL('/permissions/groups'),
            { credentials: 'include', method: 'GET' },
        );
        return (await res.json() as GroupModel[]);
    },
    toggleGroupRole: async (groupId: number, roleId: number): Promise<void> => {
        void fetch(coreURL('/permissions/role?' + new URLSearchParams({ groupId: groupId.toString(), roleId: roleId.toString() }).toString()),
            { credentials: 'include', method: 'POST' },
        );
    },
    toggleRoleMenuItem: async (roleId: number, menuItemId: number): Promise<void> => {
        void fetch(coreURL('/permissions/role/menuitem?' + new URLSearchParams({ roleId: roleId.toString(), menuItemId: menuItemId.toString() }).toString()),
            { credentials: 'include', method: 'POST' },
        );
    },
    toggleRolePermission: async (roleId: number, permissionId: number): Promise<void> => {
        void fetch(coreURL('/permissions/role/permission?' + new URLSearchParams({ roleId: roleId.toString(), permissionId: permissionId.toString() }).toString()),
            { credentials: 'include', method: 'POST' },
        );
    },
    createNewGroup: async (groupName: string): Promise<GroupModel[]> => {
        const res = await fetch(coreURL('/permissions/group?' + new URLSearchParams({ groupName }).toString()),
            { credentials: 'include', method: 'POST' },
        );
        return (await res.json() as GroupModel[]);
    },
    deleteGroup: async <TResponse, TErrorResponse>(groupId: number, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void): Promise<void> => {
        await formFetch<TResponse, TErrorResponse>(
            coreURL('/permissions/group?' + new URLSearchParams({ groupId: groupId.toString() }).toString()),
            { credentials: 'include', method: 'DELETE' },
            onSuccess,
            onFailure,
        );
    },
    createUser: async (data: AddUserModel): Promise<AddUserResultModel | void> => {
        const serverModel = {
            UserName: data.username,
            FullUserName: data.name,
            NewPassword: data.password,
            NewPassword2: data.repeatPassword,
            Group: data.group,
            Template: data.templateUser,
        };
        return await fetch(legacyURL('/system/AddNewUser'),
            { credentials: 'include', method: 'POST', body: JSON.stringify({ model: serverModel }), headers: { 'Content-Type': 'application/json' } },
        )
            .then(async (res) => await res.json() as AddUserResultModel)
            .catch((err) => console.error(err));
    },
    createRole: async (name: string): Promise<RoleModel[]> => {
        return await fetch(coreURL('/permissions/newRole?' + new URLSearchParams({ name }).toString()),
            { credentials: 'include', method: 'POST' },
        )
            .then(async (res) => await res.json() as RoleModel[]);
    },
    deleteRole: async <TResponse, TErrorResponse>(roleId: number, onSuccess: (res: TResponse) => void, onFailure: (res: TErrorResponse) => void): Promise<void> => {
        await formFetch<TResponse, TErrorResponse>(
            coreURL('/permissions/role?' + new URLSearchParams({ roleId: roleId.toString() }).toString()),
            { credentials: 'include', method: 'DELETE' },
            onSuccess,
            onFailure,
        );
    },
};

const reports = {
    getProceduresForOrder: async (id: number): Promise<PopupProcedureReportInformation> => {
        // NOTE: Reusing the old code-path here, to not have to reinvent this now. Should be redesigned once this is moved to ROW4 (still being used in other places in ROW1 for now)
        return await fetch(legacyURL(`/reports/GetProceduresForOrder?Order_UID=${id}`),
            { credentials: 'include', method: 'GET' })
            .then(async (res) => await res.json() as PopupProcedureReportInformation);
    },
    upgradeProcedure: async (orderProcedureId: number): Promise<{ Done: boolean }> => {
        return await fetch(legacyURL(`/Reports/UpgradeOrderProcedureStatus?OrderProcedure_UID=${orderProcedureId}`),
            { credentials: 'include', method: 'POST' })
            .then(async (res) => await res.json() as { Done: boolean });
    },
    getContentByReportId: async (reportId: number): Promise<ReportContentModel | null> =>
        await fetch(coreURL(`/Report/GetContentByReportId?${new URLSearchParams({ reportId: reportId.toString() })}`), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        }).then(async (res) => {
            return await res.json() as ReportContentModel | null;
        }),
};

export {
    EIDC,
    bulkSession, checks, eFact,
    general,
    getDepartmentForTerm,
    getNomenclatureForTerm,
    getPatientsForTerm,
    getPhysicianForTerm,
    getProceduresForTerm,
    getSelectDropDownForMotivations,
    identifiers, justifications, labels, locks, newWords, orders, physicians, procedures, radiologists, remarks, sendPrescription,
    statuses,
    nomenclatures,
    setup, technicians, textblocks,
    user, users,
    worklist, worklistDashboard,
    permissions,
    groups,
    reports,
};
